import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Briefcase, CalendarCheck, CreditCard, FileText, IndianRupee, Plus, Search,
  UserCheck, Users, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, LoadingGrid, StatCard, inr } from "@/components/bits";
import { NotificationPrefs } from "@/components/NotificationPrefs";
import api from "@/lib/api";

export default function EmployerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/employer/overview").then(({ data }) => setData(data)).catch(() => setData({}));
  }, []);

  if (!data) return <LoadingGrid count={4} />;

  const hasSub = !!data.subscription;
  const sc = data.stage_counts || {};

  return (
    <div data-testid="employer-dashboard">
      {!hasSub && (
        <div className="bg-[#F5A623]/12 border border-[#F5A623]/40 rounded-xl p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="no-subscription-banner">
          <div>
            <p className="font-heading font-semibold text-[#0B1B3D]">Choose an Employer Plan to Post a Job.</p>
            <p className="text-sm text-muted-foreground mt-1">Activate a subscription to unlock job posting, ATS and talent search.</p>
          </div>
          <Button onClick={() => navigate("/employer/plans")} className="rounded-full bg-[#0B1B3D]" data-testid="choose-plan-btn">
            View Plans
          </Button>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Open Jobs" value={data.open_jobs ?? 0} icon={Briefcase} />
        <StatCard label="Applications" value={data.total_applications ?? 0} icon={FileText} />
        <StatCard label="In Interview" value={(sc.interview || 0) + (sc.final_interview || 0)} icon={CalendarCheck} />
        <StatCard label="Joined" value={sc.joined || 0} icon={UserCheck} accent />
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 mt-5">
        <StatCard label="Employees" value={data.employees ?? 0} icon={Users} />
        <StatCard label="Pending Leaves" value={data.pending_leaves ?? 0} icon={CalendarCheck} />
        <StatCard label="Wallet Credits" value={data.wallet?.balance ?? 0} icon={Wallet} accent />
        <StatCard label="Jobs in Review" value={data.jobs_pending_review ?? 0} icon={FileText} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-white border border-border/60 rounded-xl p-6">
          <h3 className="font-heading font-semibold text-[#0B1B3D] mb-5">Quick actions</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { label: "Post a Job", to: "/employer/jobs/new", icon: Plus, testId: "qa-post-job" },
              { label: "Search Talent", to: "/employer/talent", icon: Search, testId: "qa-search-talent" },
              { label: "ATS Pipeline", to: "/employer/pipeline", icon: FileText, testId: "qa-pipeline" },
              { label: "Run Payroll", to: "/employer/people", icon: IndianRupee, testId: "qa-payroll" },
            ].map((a) => (
              <Link key={a.label} to={a.to} data-testid={a.testId}>
                <div className="border border-border/60 rounded-xl p-4 flex items-center gap-3 card-hover bg-[#F8FAFC]">
                  <div className="p-2 rounded-lg bg-[#2563EB]/10"><a.icon className="w-4 h-4 text-[#2563EB]" /></div>
                  <span className="text-sm font-semibold text-[#0B1B3D]">{a.label}</span>
                </div>
              </Link>
            ))}
          </div>

          <h3 className="font-heading font-semibold text-[#0B1B3D] mt-8 mb-4">Hiring funnel</h3>
          {data.total_applications === 0 ? (
            <EmptyState
              title="No applications yet"
              description="Post a job and approved listings will start receiving applications."
              cta="Post Your First Job"
              onCta={() => navigate("/employer/jobs/new")}
              testId="funnel-empty"
            />
          ) : (
            <div className="flex flex-wrap gap-2.5">
              {Object.entries(sc).map(([stage, count]) => (
                <div key={stage} className="border border-border/60 rounded-lg px-4 py-2.5 text-center">
                  <p className="font-heading font-bold text-lg text-[#0B1B3D]">{count}</p>
                  <p className="text-[11px] text-muted-foreground capitalize">{stage.replace(/_/g, " ")}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="subscription-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-[#0B1B3D]">Subscription</h3>
              <CreditCard className="w-4 h-4 text-muted-foreground" />
            </div>
            {hasSub ? (
              <>
                <p className="font-heading font-bold text-2xl text-[#2563EB]">{data.subscription.plan_name}</p>
                <p className="text-xs text-muted-foreground mt-1 capitalize">{data.subscription.cycle} · renews {new Date(data.subscription.expires_at).toLocaleDateString("en-IN")}</p>
                <p className="text-sm mt-3 text-muted-foreground">Up to {data.subscription.max_active_jobs} active jobs</p>
                <Link to="/employer/plans">
                  <Button variant="outline" size="sm" className="mt-4 rounded-full" data-testid="manage-plan-btn">Manage Plan</Button>
                </Link>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">No active subscription.</p>
                <Link to="/employer/plans">
                  <Button size="sm" className="mt-4 rounded-full bg-[#F5A623] text-[#0B1B3D] hover:bg-[#f7b84d] font-semibold" data-testid="activate-plan-btn">
                    Choose a Plan
                  </Button>
                </Link>
              </>
            )}
          </div>
          <div className="navy-bg rounded-xl p-6 text-white" data-testid="wallet-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold">Recruitment Wallet</h3>
              <Wallet className="w-4 h-4 text-[#F5A623]" />
            </div>
            <p className="font-heading font-extrabold text-3xl gold-text">{data.wallet?.balance ?? 0}</p>
            <p className="text-xs text-white/50 mt-1">credits available · {data.wallet?.used ?? 0} used</p>
            <Link to="/employer/billing">
              <Button size="sm" variant="outline" className="mt-4 rounded-full border-white/25 text-white hover:bg-white/10 hover:text-white" data-testid="topup-btn">
                Top up Wallet
              </Button>
            </Link>
          </div>
          <NotificationPrefs />
        </div>
      </div>
    </div>
  );
}
